import {
  ChangeEvent,
  ComponentProps,
  FC,
  memo,
  useEffect,
  useState,
} from 'react'

import { FormMetadata, FormProvider, getFormProps } from '@conform-to/react'
import { Form, useParams } from '@remix-run/react'
// eslint-disable-next-line @typescript-eslint/naming-convention
import _debounce from 'lodash/debounce'
import { z } from 'zod'

import { BackButton, PrimaryButton } from '~/components'
import {
  areasMultiSelectFormSchema,
  ButtonIconWithLabel,
  FacilitiesAndServicesSection,
  facilitiesSchema,
  keywordsSchema,
  KeywordsSection,
  monthlyCostsFormSchema,
  MonthlyCostsSection,
  NumberOfUsersMultiSelectForm,
  numberOfUsersMultiSelectFormSchema,
  OfficeTypeMultiSelectForm,
  officeTypeMultiSelectFormSchema,
  SearchFormSectionLayout,
  SearchResultCount,
} from '~/features/common'
import { twMerge } from '~/lib'

import { useMapContext, useMapDispatchContext } from '../../context'

export const drawerSearchFormSchema = z
  .object({})
  .merge(keywordsSchema)
  .merge(areasMultiSelectFormSchema)
  .merge(officeTypeMultiSelectFormSchema)
  .merge(numberOfUsersMultiSelectFormSchema)
  .merge(monthlyCostsFormSchema)
  .merge(facilitiesSchema)

export type DrawerSearchFormType = z.infer<typeof drawerSearchFormSchema>

type FormType = FormMetadata<DrawerSearchFormType>
export type FieldType = ReturnType<FormType['getFieldset']>

export type DrawerSearchFormValues = {
  officeTypes?: string[]
  facilities?: string[]
  minPeople?: string
  minMonthlyRent?: string
  maxMonthlyRent?: string
  keyword?: string
}

type MapCenterLocation = {
  lat: number
  lng: number
  radius: number | null
}

const convertToQueryParams = (
  formValues: DrawerSearchFormValues,
  initialMapCenterLocation: MapCenterLocation,
): string => {
  const data = {
    ...formValues,
    centerLatitude: initialMapCenterLocation.lat,
    centerLongitude: initialMapCenterLocation.lng,
    centerRadius: initialMapCenterLocation.radius,
  }

  const params = new URLSearchParams()
  Object.entries(data).forEach(([key, value]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return
    }

    if (Array.isArray(value)) {
      params.append(key, value.join(','))
      return
    }

    params.append(key, value.toString())
  })

  return params.toString()
}

const useSearchHitCount = (
  formValues: DrawerSearchFormValues,
  mapCenterLocation: MapCenterLocation,
) => {
  const [searchHitCount, setSearchHitCount] = useState(0)

  useEffect(() => {
    const queryParams = convertToQueryParams(formValues, mapCenterLocation)

    const fetchData = _debounce(() => {
      fetch(`/api/search/offices/?${queryParams}`, {
        method: 'GET',
      })
        .then((response) => response.json())
        .then((data) => {
          setSearchHitCount(data.count)
        })

      // 緯度経度の値の変化のたびに負荷が大きいのでdebounceを使ってfetchを遅延させている
    }, 1000)

    fetchData()

    return () => {
      fetchData.cancel()
    }

    // 地図を移動させたときのレンダリングの回数を抑制するために、mapCenterLocation.centerLatitude, mapCenterLocation.centerLongitudeは依存に含めない。
    // 地図を移動させたときにはmapCenterLocation.radiusも少し変化する挙動になっているので動作は問題ない認識。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, mapCenterLocation.radius])

  return { searchHitCount }
}

const usePrefectureSlug = () => {
  const params = useParams()
  return { prefectureSlug: params.prefectureSlug || '' }
}

type DrawerSearchFormProps = {
  officeTypeFormOptions: React.ComponentProps<
    typeof OfficeTypeMultiSelectForm
  >['options']
  facilitiesAndServicesFormOptions: React.ComponentProps<
    typeof FacilitiesAndServicesSection
  >['options']
  mapCenterLocation: MapCenterLocation
  fields: FieldType
  form: FormType
  officeTypesValue: string[]
  onChangeOfficeTypes: (value: string[]) => void
  officeTypeInputName: string
  minPeopleValue: string
  minPeopleInputName: string
  onChangeMinPeople: ComponentProps<
    typeof NumberOfUsersMultiSelectForm
  >['onChange']
  minMonthlyRentValue: string
  maxMonthlyRentValue: string
  onChangeMinMonthlyRent: (e: ChangeEvent<HTMLInputElement>) => void
  onChangeMaxMonthlyRent: (e: ChangeEvent<HTMLInputElement>) => void
  minMonthlyRentName: string
  maxMonthlyRentName: string
  onBlurMinMonthlyRent: () => void
  onBlurMaxMonthlyRent: () => void
  facilitiesValue: string[]
  onChangeFacilities: (e: ChangeEvent<HTMLInputElement>) => void
  facilitiesInputName: string
  keywordValue: string
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void
  keywordInputName: string
  watchedFormValues: DrawerSearchFormValues
  onEnterKeyword: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onBlurKeyword: () => void
}

const DrawerSearchFormComponent: FC<DrawerSearchFormProps> = ({
  officeTypeFormOptions,
  facilitiesAndServicesFormOptions,
  mapCenterLocation,
  watchedFormValues,
  officeTypesValue,
  onChangeOfficeTypes,
  officeTypeInputName,
  minPeopleValue,
  onChangeMinPeople,
  minPeopleInputName,
  minMonthlyRentValue,
  maxMonthlyRentValue,
  onChangeMinMonthlyRent,
  onChangeMaxMonthlyRent,
  minMonthlyRentName,
  maxMonthlyRentName,
  onBlurMinMonthlyRent,
  onBlurMaxMonthlyRent,
  facilitiesValue,
  onChangeFacilities,
  facilitiesInputName,
  keywordValue,
  onChangeKeyword,
  keywordInputName,
  onEnterKeyword,
  onBlurKeyword,
  fields,
  form,
}) => {
  const { currentDrawerPosition } = useMapContext()
  const { prefectureSlug } = usePrefectureSlug()
  const { onClickGoToList } = useMapDispatchContext()

  const onCloseSearchForm = () => {
    onClickGoToList()
  }

  const { searchHitCount } = useSearchHitCount(
    watchedFormValues,
    mapCenterLocation,
  )

  const isFullDrawer = currentDrawerPosition === 'full'
  const isLeftDrawer = currentDrawerPosition === 'left'

  return (
    <div
      className={twMerge(
        'relative h-full w-full bg-white',
        isLeftDrawer && 'h-[calc(100%-32px)] w-[calc(100%-32px)] p-4',
      )}
    >
      {isFullDrawer && (
        <div className="fixed inset-x-0 z-[10001] flex h-[58px] border-b border-grayBorder bg-white">
          <div className="relative flex w-full items-center justify-center">
            <h1 className="text-body-bold">条件変更</h1>
            <div className="absolute right-4">
              <ButtonIconWithLabel
                buttonType="close"
                onClick={onCloseSearchForm}
              />
            </div>
          </div>
        </div>
      )}
      {isLeftDrawer && (
        <div>
          <BackButton onClick={onCloseSearchForm}>一覧を表示</BackButton>
          <h1 className="py-4 text-[18px] font-bold leading-[140%]">
            条件変更
          </h1>
        </div>
      )}
      <section
        className={twMerge(
          'h-full overflow-y-auto',
          isLeftDrawer && 'h-[calc(100%-32px)]',
        )}
      >
        <div className={twMerge('pb-[100px]', isFullDrawer && 'pt-[60px]')}>
          <FormProvider context={form.context}>
            <Form method="post" {...getFormProps(form)}>
              <input
                name="prefectureSlug"
                type="hidden"
                value={prefectureSlug}
              />
              <input
                name="centerLatitude"
                type="hidden"
                value={mapCenterLocation.lat.toString()}
              />
              <input
                name="centerLongitude"
                type="hidden"
                value={mapCenterLocation.lng.toString()}
              />
              <input
                name="centerRadius"
                type="hidden"
                value={mapCenterLocation.radius?.toString() || ''}
              />
              <SearchFormSectionLayout
                headerClassName="md:w-full"
                layoutClassName="flex flex-col md:border-none min-w-0"
                title="オフィスタイプ"
              >
                <div className="grow p-4">
                  {/* OPTIMIZE: チェックボックスで実装する */}
                  <OfficeTypeMultiSelectForm
                    name={officeTypeInputName}
                    onChange={onChangeOfficeTypes}
                    options={officeTypeFormOptions}
                    value={officeTypesValue}
                  />
                </div>
              </SearchFormSectionLayout>
              <SearchFormSectionLayout
                headerClassName="md:w-full"
                layoutClassName="flex flex-col md:border-none"
                title="最大人数"
              >
                <div className="grow p-4">
                  <NumberOfUsersMultiSelectForm
                    name={minPeopleInputName}
                    onChange={onChangeMinPeople}
                    value={minPeopleValue}
                  />
                </div>
              </SearchFormSectionLayout>
              <MonthlyCostsSection
                headerClassName="md:w-full"
                layoutClassName="flex flex-col md:border-none"
                maxMonthlyRentName={maxMonthlyRentName}
                maxMonthlyRentValue={maxMonthlyRentValue}
                maxValueErrorMessages={fields.maxMonthlyRent.errors}
                minMonthlyRentName={minMonthlyRentName}
                minMonthlyRentValue={minMonthlyRentValue}
                minValueErrorMessages={fields.minMonthlyRent.errors}
                onBlurMaxMonthlyRent={onBlurMaxMonthlyRent}
                onBlurMinMonthlyRent={onBlurMinMonthlyRent}
                onChangeMaxMonthlyRent={onChangeMaxMonthlyRent}
                onChangeMinMonthlyRent={onChangeMinMonthlyRent}
              />
              <FacilitiesAndServicesSection
                columnCount={{ pc: 2 }}
                headerClassName="md:w-full"
                layoutClassName="flex flex-col md:border-none"
                meta={fields.facilities}
                name={facilitiesInputName}
                onChange={onChangeFacilities}
                options={facilitiesAndServicesFormOptions}
                value={facilitiesValue}
              />
              <KeywordsSection
                errorMessages={fields.keyword.errors}
                headerClassName="md:w-full"
                layoutClassName="flex flex-col md:border-none"
                name={keywordInputName}
                onBlur={onBlurKeyword}
                onChange={onChangeKeyword}
                onEnterKeyDown={onEnterKeyword}
                value={keywordValue}
              />
              <div
                className={twMerge(
                  'fixed inset-x-0 bottom-0 border-t border-grayBorder bg-white',
                  isLeftDrawer && 'w-[calc(100%-32px)]',
                )}
              >
                <div className="flex w-full justify-center gap-[5%] p-2.5">
                  <SearchResultCount
                    count={searchHitCount}
                    layout="column"
                    preCountText="このエリアのヒット件数"
                  />
                  <PrimaryButton
                    className="block w-[220px] min-w-0"
                    color="brand"
                  >
                    検索結果を表示
                  </PrimaryButton>
                </div>
              </div>
            </Form>
          </FormProvider>
        </div>
      </section>
    </div>
  )
}

export const DrawerSearchForm = memo(DrawerSearchFormComponent)
